import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Copy, Loader2 } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Sprint A — Duplicate Evaluation action. Mounts on catalog rows and on the
 * detail-page header. Navigates to the new draft's detail page on success.
 */
export function DuplicateEvaluationButton({
  questionnaireId,
  size = 'sm',
  variant = 'ghost',
  navigateOnSuccess = true,
  onDuplicated,
  testId = 'duplicate-evaluation-btn',
  label,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    e?.stopPropagation?.();
    if (busy) return;
    setBusy(true);
    try {
      const res = await adminApi.duplicateEvaluation(questionnaireId);
      toast.success(t('library.duplicate.success', { defaultValue: 'Evaluation duplicated' }), {
        description: res?.questionnaire?.code,
      });
      onDuplicated?.(res);
      if (navigateOnSuccess && res?.questionnaire?.id) {
        navigate(`/admin/questionnaires/${res.questionnaire.id}`);
      }
    } catch (err) {
      toast.error(t('library.duplicate.failed', { defaultValue: 'Could not duplicate' }), {
        description: formatApiError(err),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size={size}
      variant={variant}
      onClick={handle}
      disabled={busy}
      className="gap-1.5"
      data-testid={testId}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
      {label || t('library.duplicate.cta', { defaultValue: 'Duplicate' })}
    </Button>
  );
}

export default DuplicateEvaluationButton;
